import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, Calendar, Gauge, Fuel, Cog } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import api, { imgUrl } from "../lib/api";
import { BUSINESS } from "../lib/site";
import { CAR_STATUS_STYLE } from "./CarsPage";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

function EnquireDialog({ car }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: `I'm interested in the ${car.title} (£${Number(car.price).toLocaleString("en-GB")}). Is it still available?` });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", { ...form, type: "car_sale", product_id: car.id, product_title: car.title });
      toast.success("Enquiry sent. We will get back to you shortly.");
      setOpen(false);
    } catch (err) {
      toast.error("Could not send enquiry. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="car-enquire-btn" className="btn-primary w-full justify-center">
          ENQUIRE ABOUT THIS CAR <ArrowRight size={16} />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold text-ink">Enquire about this car</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">{car.title}</p>
        <form onSubmit={submit} className="space-y-3.5 mt-2">
          <div>
            <Label htmlFor="cenq-name" className="text-xs font-bold uppercase tracking-wider text-ink/70">Name *</Label>
            <Input id="cenq-name" data-testid="car-enquiry-name" required value={form.name} onChange={set("name")} className="bg-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cenq-phone" className="text-xs font-bold uppercase tracking-wider text-ink/70">Phone</Label>
              <Input id="cenq-phone" data-testid="car-enquiry-phone" type="tel" value={form.phone} onChange={set("phone")} className="bg-white" />
            </div>
            <div>
              <Label htmlFor="cenq-email" className="text-xs font-bold uppercase tracking-wider text-ink/70">Email</Label>
              <Input id="cenq-email" data-testid="car-enquiry-email" type="email" value={form.email} onChange={set("email")} className="bg-white" />
            </div>
          </div>
          <div>
            <Label htmlFor="cenq-msg" className="text-xs font-bold uppercase tracking-wider text-ink/70">Message *</Label>
            <Textarea id="cenq-msg" data-testid="car-enquiry-message" required rows={4} value={form.message} onChange={set("message")} className="bg-white" />
          </div>
          <button type="submit" data-testid="car-enquiry-submit-btn" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60">
            {busy ? "SENDING..." : "SEND ENQUIRY"} <ArrowRight size={16} />
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CarPage() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setActiveImg(0);
    api.get(`/cars/${id}`).then((res) => setCar(res.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="container-pr7 py-24 text-center" data-testid="car-not-found">
        <h1 className="font-heading font-extrabold text-ink text-3xl">Car not found</h1>
        <p className="text-muted-foreground mt-3">This vehicle may have been sold or removed.</p>
        <Link to="/cars-for-sale" className="btn-primary mt-6 inline-flex">BROWSE CARS <ArrowRight size={16} /></Link>
      </div>
    );
  }

  if (!car) {
    return <div className="container-pr7 py-24"><div className="h-96 bg-white border border-border rounded-lg animate-pulse" /></div>;
  }

  const imgs = car.images?.length ? car.images : [];
  const current = imgs[activeImg] || imgs[car.main_image] || imgs[0];

  return (
    <>
      <SEO
        title={`${car.title} | Cars For Sale - PR7 MOT Centre Adlington`}
        description={`${car.title} for sale at PR7 MOT Centre, Adlington - £${Number(car.price).toLocaleString("en-GB")}. Call 01257 786 540 to arrange a viewing.`}
        path={`/cars-for-sale/${car.id}`}
      />

      <section className="bg-surface py-10 lg:py-14">
        <div className="container-pr7">
          <Link to="/cars-for-sale" data-testid="back-to-cars" className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-sky mb-7 transition-colors">
            <ArrowLeft size={15} /> Back to cars for sale
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="relative bg-white border border-border rounded-lg overflow-hidden aspect-[16/10]">
                {current ? (
                  <img src={imgUrl(current)} alt={car.title} data-testid="car-main-image" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">No image available</div>
                )}
                <span className={`absolute top-4 left-4 status-chip ${CAR_STATUS_STYLE[car.status]}`} data-testid="car-status">{car.status}</span>
              </div>
              {imgs.length > 1 && (
                <div className="flex gap-3 mt-3 overflow-x-auto pb-1">
                  {imgs.map((img, i) => (
                    <button
                      key={i}
                      data-testid={`car-thumb-${i}`}
                      onClick={() => setActiveImg(i)}
                      className={`shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-colors ${i === activeImg ? "border-brand" : "border-border hover:border-sky/50"}`}
                    >
                      <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="eyebrow mb-2">Car For Sale</p>
              <h1 className="font-heading font-extrabold text-ink text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="car-title">{car.title}</h1>
              <p className="font-mono font-semibold text-brand text-3xl mt-4" data-testid="car-price">£{Number(car.price).toLocaleString("en-GB")}</p>

              <div className="bg-white border border-border rounded-lg divide-y divide-border mt-7 text-sm">
                {car.year && (
                  <div className="flex justify-between px-5 py-3"><span className="text-muted-foreground inline-flex items-center gap-2"><Calendar size={14} className="text-brand" />Year</span><span className="font-medium text-ink">{car.year}</span></div>
                )}
                {car.mileage && (
                  <div className="flex justify-between px-5 py-3"><span className="text-muted-foreground inline-flex items-center gap-2"><Gauge size={14} className="text-brand" />Mileage</span><span className="font-medium text-ink">{car.mileage}</span></div>
                )}
                {car.fuel && (
                  <div className="flex justify-between px-5 py-3"><span className="text-muted-foreground inline-flex items-center gap-2"><Fuel size={14} className="text-brand" />Fuel</span><span className="font-medium text-ink">{car.fuel}</span></div>
                )}
                {car.transmission && (
                  <div className="flex justify-between px-5 py-3"><span className="text-muted-foreground inline-flex items-center gap-2"><Cog size={14} className="text-brand" />Transmission</span><span className="font-medium text-ink">{car.transmission}</span></div>
                )}
              </div>

              <div className="mt-7 space-y-3">
                <EnquireDialog car={car} />
                <a href={BUSINESS.phoneHref} data-testid="car-call-btn" className="btn-outline-light !text-ink !border-brand/40 hover:!bg-pale w-full justify-center">
                  <Phone size={16} className="text-brand" /> CALL {BUSINESS.phone}
                </a>
              </div>
            </div>
          </div>

          {car.description && (
            <div className="bg-white border border-border rounded-lg p-7 mt-12">
              <h2 className="font-heading font-extrabold text-ink text-xl tracking-tight">About this car</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mt-3 whitespace-pre-line" data-testid="car-description">{car.description}</p>
            </div>
          )}
        </div>
      </section>

      <div className="fixed bottom-16 inset-x-0 z-30 md:hidden px-3" data-testid="car-mobile-cta">
        <a href={BUSINESS.phoneHref} data-testid="car-mobile-call" className="flex items-center justify-center gap-2 bg-brand text-white font-heading font-bold text-sm py-3.5 rounded shadow-xl">
          <Phone size={16} /> CALL NOW - {BUSINESS.phone}
        </a>
      </div>
    </>
  );
}
